/**
 * useStudioShortcuts — global keyboard shortcuts + browser-push notifications.
 *
 * Keyboard shortcuts (registered when ⌘/Ctrl is held):
 *   ⌘K  Open global search
 *   G D  Go to Dashboard (Command Center)
 *   G T  Go to AI Topics
 *   G R  Go to Reports
 *   G P  Go to Content Pillars
 *   G B  Go to Billing
 *   ?    Show shortcuts help
 *
 * Browser-push notifications:
 *   - Requests Notification permission once when the user lands on a Studio route
 *   - Polls /api/studio/notifications/unread every 60s
 *   - Fires a browser Notification for any new in-app notification since last seen
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || '';

const ROUTES = {
  d: "/studio",
  t: "/studio/topic-suggestions",
  r: "/studio/reports",
  p: "/studio/pillars",
  b: "/studio/billing",
  a: "/studio/analytics",
  c: "/studio/calendar",
  m: "/studio/my-tasks",
};

export function useStudioShortcuts() {
  const navigate = useNavigate();
  const [helpOpen, setHelpOpen] = useState(false);
  const gMode = useRef(false);
  const lastSeenId = useRef(null);

  // 1. Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      // Ignore when typing in inputs / contenteditable
      const tag = (e.target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || e.target.isContentEditable) return;

      // ⌘/Ctrl + K → search
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        navigate("/studio/search");
        return;
      }

      // ?  → help
      if (e.key === "?") {
        e.preventDefault();
        setHelpOpen((s) => !s);
        return;
      }

      // g then x  → navigate
      if (gMode.current) {
        const dest = ROUTES[e.key.toLowerCase()];
        gMode.current = false;
        if (dest) {
          e.preventDefault();
          navigate(dest);
        }
        return;
      }
      if (e.key.toLowerCase() === "g") {
        gMode.current = true;
        // Auto-cancel g mode after 1.5s
        setTimeout(() => { gMode.current = false; }, 1500);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  // 2. Browser-push notifications: ask once, then poll
  useEffect(() => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission === "default") {
      // Defer slightly so the prompt doesn't block initial render.
      const t = setTimeout(() => {
        try { Notification.requestPermission(); } catch (e) { /* ignore */ }
      }, 4000);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !("Notification" in window)) return;

    const poll = async () => {
      try {
        const r = await axios.get(`${API}/api/studio/notifications`, {
          withCredentials: true, params: { limit: 5, unseen: 1 },
        });
        const items = r.data?.items || r.data?.notifications || [];
        if (!items.length) return;
        const latest = items[0];
        const id = latest._id || latest.id;
        if (!id || id === lastSeenId.current) return;
        lastSeenId.current = id;
        if (Notification.permission === "granted") {
          try {
            const n = new Notification(latest.title || "MarkeeMix Studio", {
              body: latest.body || latest.message || "",
              icon: "/favicon-32x32.png",
              tag: id,
            });
            n.onclick = () => {
              window.focus();
              const url = latest.link || latest.url || "/studio";
              navigate(url.startsWith("http") ? "/studio" : url);
              n.close();
            };
          } catch (e) { /* ignore */ }
        }
      } catch (e) {
        // 401 → user not signed in; silently stop polling
      }
    };
    const interval = setInterval(poll, 60_000);
    poll();
    return () => clearInterval(interval);
  }, [navigate]);

  return { helpOpen, setHelpOpen };
}

export function ShortcutsHelp({ open, onClose }) {
  if (!open) return null;
  const groups = [
    { label: "Navigate", items: [
      ["⌘K", "Open global search"],
      ["G D", "Dashboard (Command Center)"],
      ["G A", "Analytics"],
      ["G T", "AI Topic Suggestions"],
      ["G C", "Calendar"],
      ["G M", "My Tasks"],
      ["G R", "Reports"],
      ["G P", "Content Pillars"],
      ["G B", "Billing"],
    ]},
    { label: "Misc", items: [
      ["?", "Toggle this help"],
    ]},
  ];
  return (
    <div className="studio-vid-overlay" onClick={onClose} data-testid="shortcuts-help">
      <div className="studio-vid-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <header>
          <h3>Keyboard shortcuts</h3>
          <button className="studio-btn-ghost" onClick={onClose} data-testid="shortcuts-close" style={{ padding: "4px 10px" }}>✕</button>
        </header>
        <div style={{ padding: 18 }}>
          {groups.map((g) => (
            <div key={g.label} style={{ marginBottom: 14 }}>
              <p className="studio-eyebrow" style={{ marginBottom: 8 }}>{g.label}</p>
              {g.items.map(([k, l]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--st-line)" }}>
                  <span style={{ color: "var(--st-text-soft)" }}>{l}</span>
                  <kbd style={{
                    background: "var(--st-bg-2)", border: "1px solid var(--st-line)",
                    padding: "2px 8px", borderRadius: 6, fontSize: 12,
                    fontFamily: "monospace", color: "var(--st-purple)",
                  }}>{k}</kbd>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
